// src/components/apps/SharePoint/FolderPickerDialog.jsx
import React, { useState, useEffect, useCallback } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  List,
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  CircularProgress,
  Typography,
  Breadcrumbs,
  Link,
  Alert,
  Box,
} from "@mui/material";
import FolderIcon from "@mui/icons-material/Folder";
import HomeIcon from "@mui/icons-material/Home";
import NavigateNextIcon from "@mui/icons-material/NavigateNext";
import DriveFileMoveIcon from "@mui/icons-material/DriveFileMove";

const FolderPickerDialog = ({
  open,
  item,
  onClose,
  onConfirm,
  loading,
  listFolders,
  rootLabel = "Documents",
  currentFolderId,
}) => {
  const [pathStack, setPathStack] = useState([]);
  const [folders, setFolders] = useState([]);
  const [loadingFolders, setLoadingFolders] = useState(false);
  const [error, setError] = useState(null);
  const [selectedFolderId, setSelectedFolderId] = useState(null);

  const browseFolderId = pathStack.length > 0 ? pathStack[pathStack.length - 1].id : null;

  const fetchFolders = useCallback(async () => {
    setLoadingFolders(true);
    setError(null);
    try {
      const data = await listFolders(browseFolderId);
      // Filter out the item being moved (can't move into itself)
      const filtered = data.filter((f) => f.id !== item?.id);
      setFolders(filtered);
    } catch (err) {
      console.error("Error loading folders:", err);
      setError(err.message);
      setFolders([]);
    } finally {
      setLoadingFolders(false);
    }
  }, [browseFolderId, listFolders, item?.id]);

  useEffect(() => {
    if (open) {
      setPathStack([]);
      setSelectedFolderId(null);
      setError(null);
    }
  }, [open]);

  useEffect(() => {
    if (open) {
      fetchFolders();
    }
  }, [open, fetchFolders]);

  const handleFolderClick = (folder) => {
    setPathStack((prev) => [...prev, { id: folder.id, name: folder.name }]);
    setSelectedFolderId(null);
  };

  const handleBreadcrumbNavigate = (index) => {
    if (index === -1) {
      setPathStack([]);
    } else {
      setPathStack((prev) => prev.slice(0, index + 1));
    }
    setSelectedFolderId(null);
  };

  const handleSelectFolder = (folderId) => {
    setSelectedFolderId(folderId === selectedFolderId ? null : folderId);
  };

  const handleConfirm = () => {
    // If a folder is selected, move into that folder
    // Otherwise, move into the currently browsed folder
    const destinationId = selectedFolderId || browseFolderId;
    onConfirm(destinationId);
  };

  // Check if destination is same as current location
  const isSameLocation = (browseFolderId || null) === (currentFolderId || null) && !selectedFolderId;

  if (!item) return null;

  return (
    <Dialog open={open} onClose={loading ? undefined : onClose} maxWidth="sm" fullWidth>
      <DialogTitle sx={{ display: "flex", alignItems: "center", gap: 1 }}>
        <DriveFileMoveIcon color="primary" />
        Move "{item.name}"
      </DialogTitle>
      <DialogContent>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          Select a destination folder:
        </Typography>

        {/* Breadcrumb */}
        <Breadcrumbs separator={<NavigateNextIcon fontSize="small" />} sx={{ mb: 2 }}>
          <Link
            component="button"
            variant="body2"
            underline="hover"
            sx={{ display: "flex", alignItems: "center", cursor: "pointer" }}
            onClick={() => handleBreadcrumbNavigate(-1)}
          >
            <HomeIcon sx={{ mr: 0.5 }} fontSize="small" />
            {rootLabel}
          </Link>
          {pathStack.map((folder, index) => {
            const isLast = index === pathStack.length - 1;
            return isLast ? (
              <Typography key={folder.id} color="text.primary" variant="body2">
                {folder.name}
              </Typography>
            ) : (
              <Link
                key={folder.id}
                component="button"
                variant="body2"
                underline="hover"
                sx={{ cursor: "pointer" }}
                onClick={() => handleBreadcrumbNavigate(index)}
              >
                {folder.name}
              </Link>
            );
          })}
        </Breadcrumbs>

        {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

        {loadingFolders ? (
          <Box sx={{ display: "flex", justifyContent: "center", py: 4 }}>
            <CircularProgress />
          </Box>
        ) : (
          <List sx={{ maxHeight: 300, overflow: "auto", border: 1, borderColor: "divider", borderRadius: 1 }}>
            {folders.length === 0 ? (
              <ListItem>
                <ListItemText
                  primary="No subfolders"
                  secondary="You can move the item to this folder"
                  sx={{ textAlign: "center", color: "text.secondary" }}
                />
              </ListItem>
            ) : (
              folders.map((folder) => (
                <ListItem key={folder.id} disablePadding>
                  <ListItemButton
                    selected={selectedFolderId === folder.id}
                    onClick={() => handleSelectFolder(folder.id)}
                    onDoubleClick={() => handleFolderClick(folder)}
                  >
                    <ListItemIcon>
                      <FolderIcon color="warning" />
                    </ListItemIcon>
                    <ListItemText primary={folder.name} />
                  </ListItemButton>
                </ListItem>
              ))
            )}
          </List>
        )}

        <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: "block" }}>
          Click to select, double-click to open folder
        </Typography>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={loading}>
          Cancel
        </Button>
        <Button
          onClick={handleConfirm}
          variant="contained"
          disabled={loading || isSameLocation}
          startIcon={loading ? <CircularProgress size={16} color="inherit" /> : <DriveFileMoveIcon />}
        >
          {loading ? "Moving..." : selectedFolderId ? "Move to Selected" : "Move Here"}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default FolderPickerDialog;
